import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    Stack,
    SvgIconTypeMap,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';

import { Language, Message } from 'dg-web-shared/lib/Localized.ts';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization.tsx';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useEasyForm } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';
import { GenericFormSubmitError } from './ParkingaboUsersUtils.tsx';
import { AutoAwesome, Circle, Person } from '@mui/icons-material';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import {
    ValidationData,
    FormGlobalViolationEntry,
    isFormGlobalViolationEntry,
} from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { IconForCustomerStatus } from './OperatorParkingaboUsers.tsx';
import { OperatorTypo } from '../ui/OperatorTypo.ts';
import { ColorHex } from '../ui/Colors.ts';
import { css } from '@emotion/css';
import { Control, Controller } from 'react-hook-form';
import * as EmailValidator from 'email-validator';
import {
    CustomerTenantLegalForm,
    getCustomerDisplayName,
} from 'dg-web-shared/common/models/Users.tsx';
import { Colors } from 'dg-web-shared/ui/vars.ts';
import InfoIcon from '@mui/icons-material/Info';
import React, { type JSX, useEffect, useState } from 'react';
import { OperatorFormSelect } from '../ui/form/OperatorFormSelect.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import {
    DetailHeader,
    DetailTabs,
    ParkingaboUserInfoTab,
    useParkingaboUserDetailOutletContext,
} from './OperatorParkingaboUsersDetail.tsx';
import { useParkingaboUserFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { ShouldConfirmBeforeExitFormProps } from 'dg-web-shared/common/utils/FormDefinitions.tsx';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import Alert from '@mui/material/Alert';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import {
    CustomerTenantState,
    getTranslationOfCustomerState,
} from 'product-shared/parkingabo/CustomerTenantState.ts';
import {
    mandatoryFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { parkingaboUrl } from '../utils/UrlUtil.ts';
import { ParkingaboRole } from '../parkingabo-role/OperatorParkingaboRole.ts';
import {
    VehicleIdentification,
    ParkingaboCustomerPaymentMode,
} from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { EmailSuppressionTag } from '../shared-mui-components/EmailSuppressionTag.tsx';
import { EmailSuppression } from '../common/models/EmailSuppression.ts';

interface ParkingaboUserFormData {
    language: Language;
    firstName: string | null;
    lastName: string | null;
    company1: string | null;
    company2: string | null;
    roleId: string | null;
    email: string | null;
    phone: string | null;
    reference: string | null;
    remarks: string | null;
    legalForm: CustomerTenantLegalForm;
    initialBalance: InitialBalanceFormData | null;
}

interface InitialBalanceFormData {
    amount: number;
    remark: string;
}

interface CreateParkingaboUserInitialValues extends ParkingaboUserFormData {
    customerNr: string | null;
    state: CustomerTenantState | null;
    paymentMeansState: PaymentMeansState | null;
    logins: ParkingaboLogin[] | null;
}

const enum PaymentMeansState {
    VALID = 'VALID',
    INVALID = 'INVALID',
    MISSING = 'MISSING',
}

export enum TenantAllowedCustomerLegalForm {
    PRIVATE_ONLY = 'PRIVATE_ONLY',
    COMPANY_ONLY = 'COMPANY_ONLY',
    PRIVATE_AND_COMPANY = 'PRIVATE_AND_COMPANY',
}

export enum ParkingaboLoginState {
    CREATED = 'CREATED',
    CONFIRMED = 'CONFIRMED',
    DELETED = 'DELETED',
}

export interface ParkingaboLogin {
    loginId: string;
    email: string;
    requestedEmail: string | null;
    state: ParkingaboLoginState;
    emailSuppression: EmailSuppression | null;
    requestedEmailSuppression: EmailSuppression | null;
}

export enum ParkingaboCustomerSaldoState {
    NEGATIVE = 'NEGATIVE',
    ZERO = 'ZERO',
    POSITIVE = 'POSITIVE',
}

export interface ParkingaboUser extends CreateParkingaboUserInitialValues {
    customerNr: string;
    state: CustomerTenantState;
    logins: ParkingaboLogin[];
    onboardingCompleteTime: string | null;
    hasActiveOrFutureProducts: boolean;
    customerSaldoState: ParkingaboCustomerSaldoState;
    hasAppLogin: boolean;
    hasActiveAlias: boolean;
    hasSharedLogin: boolean;
    canReactivateCustomer: boolean;
    roleId: string | null;
    role: ParkingaboRole;
    needsProduct: boolean;
    needsVehicleWithBadge: boolean;
    isAllowedInitialBalanceCreation: boolean;
    vehiclesWithMissingLps: number;
    vehiclesCount: number;
    runningEntryWithoutExitCount: number;
    externalVehicleUrlToken: string | null;
    autoWrittenOffAt: string | null;
    autoAppDeletedAt: string | null;
    archivedAt: string | null;
    emailSuppression: EmailSuppression | null;
}

enum Fields {
    ID = 'customerNr',
    State = 'state',
    PaymentMeansState = 'paymentMeansState',
    FirstName = 'firstName',
    LastName = 'lastName',
    Company1 = 'company1',
    Company2 = 'company2',
    RoleId = 'roleId',
    Reference = 'reference',
    Remarks = 'remarks',
    Language = 'language',
    Email = 'email',
    Phone = 'phone',
    LegalForm = 'legalForm',
    InitialBalance = 'initialBalance',
}

enum InitialBalanceFields {
    Amount = 'amount',
    Remark = 'remark',
}

export type FieldValues = {
    [Fields.Language]: Language;
    [Fields.FirstName]: string | null;
    [Fields.LastName]: string | null;
    [Fields.Company1]: string | null;
    [Fields.Company2]: string | null;
    [Fields.RoleId]: string | null;
    [Fields.Email]: string | null;
    [Fields.Phone]: string | null;
    [Fields.Reference]: string | null;
    [Fields.Remarks]: string | null;
    [Fields.ID]: string | null;
    [Fields.State]: CustomerTenantState | null;
    [Fields.PaymentMeansState]: PaymentMeansState | null;
    [Fields.LegalForm]: CustomerTenantLegalForm;
    [Fields.InitialBalance]: InitialBalanceFormData;
};

export function OperatorParkingaboUserEditForm() {
    const {
        parkingaboUser,
        refetchParkingaboUser,
        roles,
        onDirtyStateChange,
        onClose,
    } = useParkingaboUserDetailOutletContext();
    const [updateParkingaboUserState, updateParkingaboUser] = useServerWrite<
        {
            customerNr: string;
        } & ParkingaboUserFormData,
        ParkingaboUser,
        ValidationData
    >(payload => ({
        url: `/ui-api/operator-account/parkingabo/customer/${payload.customerNr}`,
        method: RequestMethod.PUT,
    }));

    const { refetchList } = useParkingaboUserFilterConfiguration();
    useServerSuccessEffect(updateParkingaboUserState, () => {
        refetchParkingaboUser();
        refetchList();
    });

    return (
        <OperatorParkingaboUserForm
            title={getCustomerDisplayName(parkingaboUser, false)}
            Icon={Person}
            parkingaboUser={parkingaboUser}
            onSubmit={payload =>
                updateParkingaboUser({
                    customerNr: parkingaboUser.customerNr,
                    ...payload,
                    initialBalance:
                        payload.initialBalance?.amount &&
                        !parkingaboUser.initialBalance
                            ? payload.initialBalance
                            : null,
                })
            }
            submitState={updateParkingaboUserState}
            readonly={parkingaboUser.state === CustomerTenantState.ARCHIVED}
            roleOfUser={parkingaboUser.role}
            roles={roles}
            onDirtyStateChange={onDirtyStateChange}
            onClose={onClose}
            detailHeader={<DetailHeader />}
            detailBar={
                <DetailTabs
                    parkingaboUserInfoTab={ParkingaboUserInfoTab.DETAILS}
                    roleHasAppPayment={
                        parkingaboUser.role.paymentMode ===
                        ParkingaboCustomerPaymentMode.APP_PAYMENT
                    }
                />
            }
        />
    );
}

interface OperatorParkingaboUserCreateFormProps extends ShouldConfirmBeforeExitFormProps {
    onCreated: (newCustomerNr: string) => void;
    roleOfUser: ParkingaboRole;
    roles: ParkingaboRole[];
}

export function OperatorParkingaboUserCreateForm({
    roleOfUser,
    roles,
    onCreated,
    onClose,
    onDirtyStateChange,
}: OperatorParkingaboUserCreateFormProps) {
    const [createState, createRequest] = useServerWrite<
        ParkingaboUserFormData,
        ParkingaboUser,
        ValidationData
    >(() => ({
        url: '/ui-api/operator-account/parkingabo/customer',
    }));

    useServerSuccessEffect(createState, data => onCreated(data.customerNr));

    return (
        <OperatorParkingaboUserForm
            title={
                <Localized
                    de="Neuer Benutzer"
                    fr="Nouveau utilisateur"
                    it="Nuovo utente"
                    en="New user"
                />
            }
            Icon={Person}
            onSubmit={payload =>
                createRequest({
                    ...payload,
                    initialBalance: !payload.initialBalance?.amount
                        ? null
                        : payload.initialBalance,
                })
            }
            submitState={createState}
            roleOfUser={roleOfUser}
            roles={roles}
            readonly={false}
            onClose={onClose}
            onDirtyStateChange={onDirtyStateChange}
        />
    );
}

export function OperatorParkingaboUserInviteForm({
    roleOfUser,
    roles,
    onCreated,
    onClose,
    onDirtyStateChange,
}: OperatorParkingaboUserCreateFormProps) {
    const [createState, createRequest] = useServerWrite<
        ParkingaboUserFormData,
        ParkingaboUser,
        ValidationData
    >(() => ({
        url: '/ui-api/operator-account/parkingabo/customer',
    }));

    useServerSuccessEffect(createState, data => onCreated(data.customerNr));

    return (
        <OperatorParkingaboUserForm
            title={
                <Localized
                    de="Benutzer einladen"
                    fr="Inviter l'utilisateur"
                    it="Invita utente"
                    en="Invite user"
                />
            }
            Icon={AutoAwesome}
            onSubmit={payload =>
                createRequest({
                    ...payload,
                    initialBalance: !payload.initialBalance?.amount
                        ? null
                        : payload.initialBalance,
                })
            }
            submitState={createState}
            roleOfUser={roleOfUser}
            roles={roles}
            readonly={false}
            onClose={onClose}
            onDirtyStateChange={onDirtyStateChange}
        />
    );
}

interface OperatorParkingaboUserFormProps {
    title: React.ReactNode;
    Icon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>> & {
        muiName: string;
    };
    parkingaboUser?: ParkingaboUser;
    onSubmit?: (formData: ParkingaboUserFormData) => void;
    submitState: ServerRequestState<ParkingaboUser, ValidationData>;
    readonly: boolean;
    roleOfUser: ParkingaboRole;
    roles: ParkingaboRole[];
    onClose: () => void;
    onDirtyStateChange?: (dirty: boolean) => void;
    detailHeader?: JSX.Element;
    detailBar?: JSX.Element;
}

function getTranslationForPaymeantState(
    state: PaymentMeansState | null | undefined,
): Message {
    if (!state) return { de: '', fr: '', it: '', en: '' };
    switch (state) {
        case PaymentMeansState.VALID:
            return {
                de: 'GÜLTIG',
                fr: 'VALABLE',
                it: 'VALIDO',
                en: 'VALID',
            };
        case PaymentMeansState.INVALID:
            return {
                de: 'NICHT GÜLTIG',
                fr: 'PAS VALIDE',
                it: 'NON VALIDO',
                en: 'INVALID',
            };

        case PaymentMeansState.MISSING:
            return {
                de: 'NICHT VORHANDEN',
                fr: 'PAS EXISTANT',
                it: 'NON PRESENTE',
                en: 'NOT AVAILABLE',
            };
    }
}

function FirstNameLastNameFields({
    readonly,
    control,
    longTooManyCharsMessage,
    mandatoryFieldMessage,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    longTooManyCharsMessage: string;
    mandatoryFieldMessage: string;
}) {
    return (
        <Stack spacing={2} direction="row">
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized de="Name" fr="Nom" it="Cognome" en="Lastname" />
                }
                name={Fields.LastName}
                autoFocus={true}
                style={{
                    flexGrow: 1,
                }}
                control={control}
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                    maxLength: {
                        value: MAX_LENGTH_LONG,
                        message: longTooManyCharsMessage,
                    },
                }}
                trimOnBlur={true}
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized
                        de="Vorname"
                        fr="Prénom"
                        it="Nome"
                        en="Firstname"
                    />
                }
                name={Fields.FirstName}
                style={{
                    flexGrow: 1,
                }}
                control={control}
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                    maxLength: {
                        value: MAX_LENGTH_LONG,
                        message: longTooManyCharsMessage,
                    },
                }}
                trimOnBlur={true}
            />
        </Stack>
    );
}

function ParkingaboUserLifecycleInfo({
    autoWrittenOffAt,
    archivedAt,
}: {
    autoWrittenOffAt: string | null;
    archivedAt: string | null;
}) {
    if (archivedAt != null) {
        return (
            <Alert severity="info">
                <Localized
                    de={`Das Konto wurde am ${Formatter.dayMonthYear(
                        DateTime.fromISO(archivedAt),
                    )} archiviert.`}
                    fr={`Le compte a été archivé le ${Formatter.dayMonthYear(
                        DateTime.fromISO(archivedAt),
                    )}.`}
                    it={`Il conto è stato archiviato il ${Formatter.dayMonthYear(
                        DateTime.fromISO(archivedAt),
                    )}.`}
                    en={`The account was archived on ${Formatter.dayMonthYear(
                        DateTime.fromISO(archivedAt),
                    )}.`}
                />
            </Alert>
        );
    }
    if (autoWrittenOffAt != null) {
        return (
            <Alert severity="info">
                <Localized
                    de={`Am ${Formatter.dayMonthYear(
                        DateTime.fromISO(autoWrittenOffAt),
                    )} wurde automatisch das Konto gesperrt und der Saldo auf Null gesetzt.`}
                    fr={`Le compte a été bloqué et le solde remis à zéro autom. le ${Formatter.dayMonthYear(
                        DateTime.fromISO(autoWrittenOffAt),
                    )}.`}
                    it={`Il conto è stato bloccato e il saldo azzerato autom. il ${Formatter.dayMonthYear(
                        DateTime.fromISO(autoWrittenOffAt),
                    )}.`}
                    en={`On ${Formatter.dayMonthYear(
                        DateTime.fromISO(autoWrittenOffAt),
                    )}, the account was locked and the balance was set to zero automatically.`}
                />
            </Alert>
        );
    }
}

function StateAndIdFields({
    parkingaboUser,
    control,
    statusTranslation,
    roleOfUser,
}: {
    parkingaboUser?: ParkingaboUser;
    control: Control<FieldValues, object>;
    statusTranslation: string;
    roleOfUser: ParkingaboRole;
}) {
    return (
        <Stack spacing={2} direction="row">
            {parkingaboUser?.state && (
                <TextField
                    disabled={true}
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Localized
                                de="Zustand"
                                fr="État"
                                it="Stato"
                                en="State"
                            />
                            <Tooltip
                                title={
                                    roleOfUser.paymentMode ===
                                    ParkingaboCustomerPaymentMode.APP_PAYMENT ? (
                                        <CustomerStateWithAppInfo />
                                    ) : (
                                        <CustomerStateWithoutAppInfo />
                                    )
                                }
                                placement="right"
                            >
                                <InfoIcon
                                    sx={{
                                        color: theme => theme.palette.blue.main,
                                        marginLeft: 1,
                                        fontSize: 20,
                                    }}
                                />
                            </Tooltip>
                        </Box>
                    }
                    defaultValue={statusTranslation}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconForCustomerStatus
                                        status={parkingaboUser.state}
                                        onboardingCompleteTime={
                                            parkingaboUser.onboardingCompleteTime
                                        }
                                        paymentMode={roleOfUser.paymentMode}
                                    />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            )}
            {parkingaboUser?.customerNr && (
                <ReactHookFormTextField
                    readonly={true}
                    label={<Localized de="ID" fr="ID" it="ID" en="ID" />}
                    name={Fields.ID}
                    control={control}
                />
            )}
        </Stack>
    );
}

function CustomerStateWithAppInfo() {
    return (
        <Localized
            de={
                <>
                    <b>PENDENT</b>
                    <ul>
                        <li>Registrierung in der App nicht abgeschlossen</li>
                        <li>nur Parkvorgänge ohne Tarif möglich</li>
                        <li>bestehende Produkte sind weiterhin gültig</li>
                    </ul>
                    <b>AKTIV</b>
                    <br />
                    <b>gültige Zahlungsmittel</b>
                    <ul>
                        <li className={css({ fontStyle: 'italic' })}>
                            keine Einschränkung
                        </li>
                    </ul>
                    <b>ungültige/keine Zahlungsmittel</b>
                    <ul>
                        <li>Zugang zur App möglich</li>
                        <li>nur Parkvorgänge ohne Tarif möglich</li>
                        <li>
                            es können keine neuen Produkte in der App gekauft
                            werden
                        </li>
                        <li>bestehende Produkte sind weiterhin gültig</li>
                    </ul>
                    <b>GESPERRT</b>
                    <ul>
                        <li>Zugang zur App möglich</li>
                        <li>
                            es können keine neuen Produkte in der App gekauft
                            werden
                        </li>
                        <li>kein Parkvorgang möglich</li>
                        <li>bestehende Produkte sind ausgesetzt</li>
                    </ul>
                    <b>ARCHIVIERT</b>
                    <ul>
                        <li>Zugang zur App nicht möglich</li>
                        <li>kein Parkvorgang möglich</li>
                    </ul>
                </>
            }
            fr={
                <>
                    <b>EN ATTENTE</b>
                    <ul>
                        <li>inscription dans l&apos;app pas complète</li>
                        <li>eules les procédures sans tarif sont possibles</li>
                        <li>les produits existants restent valables</li>
                    </ul>
                    <b>ACTIF</b>
                    <br />
                    <b>moyen de paiement valide</b>
                    <ul>
                        <li className={css({ fontStyle: 'italic' })}>
                            aucune restriction
                        </li>
                    </ul>
                    <b>moyen de paiement non valide/manquant</b>
                    <ul>
                        <li>accès à l&apos;app possible</li>
                        <li>seules les procédures sans tarif sont possibles</li>
                        <li>
                            impossible d&apos;acheter de nouveaux produits dans
                            l&apos;app
                        </li>
                        <li>les produits existants restent valables</li>
                    </ul>
                    <b>VERROUILLÉ</b>
                    <ul>
                        <li>accès à l&apos;app possible</li>
                        <li>
                            impossible d&apos;acheter de nouveaux produits dans
                            l&apos;app
                        </li>
                        <li>impossible d’effectuer des procédures</li>
                        <li>les produits existants sont suspendus</li>
                    </ul>
                    <b>ARCHIVÉ</b>
                    <ul>
                        <li>accès à l&apos;app impossible</li>
                        <li>impossible d’effectuer des procédures</li>
                    </ul>
                </>
            }
            it={
                <>
                    <b>IN SOSPESO</b>
                    <ul>
                        <li>registrazione nell&apos;app non completata</li>
                        <li>sono possibili solo soste senza tariffa</li>
                        <li>i prodotti esistenti rimangono validi</li>
                    </ul>
                    <b>ATTIVO</b>
                    <br />
                    <b>mezzo di pagamento valido</b>
                    <ul>
                        <li className={css({ fontStyle: 'italic' })}>
                            nessuna restrizione
                        </li>
                    </ul>
                    <b>mezzo di pagamento non valido/assente</b>
                    <ul>
                        <li>accesso all&apos;app possibile</li>
                        <li>sono possibili solo soste senza tariffa</li>
                        <li>
                            non è possibile acquistare nuovi prodotti
                            nell&apos;app
                        </li>
                        <li>i prodotti esistenti rimangono validi</li>
                    </ul>
                    <b>BLOCCATO</b>
                    <ul>
                        <li>accesso all&apos;app possibile</li>
                        <li>
                            non è possibile acquistare nuovi prodotti nell’app
                        </li>
                        <li>non è possibile effettuare soste</li>
                        <li>i prodotti esistenti sono sospesi</li>
                    </ul>
                    <b>ARCHIVIATO</b>
                    <ul>
                        <li>accesso all’app non possibile</li>
                        <li>non è possibile effettuare soste</li>
                    </ul>
                </>
            }
            en={
                <>
                    <b>PENDING</b>
                    <ul>
                        <li>registration in the app not completed</li>
                        <li>
                            only parking sessions without a tariff are possible
                        </li>
                        <li>existing products remain valid</li>
                    </ul>
                    <b>ACTIVE</b>
                    <br />
                    <b>Valid payment method</b>
                    <ul>
                        <li className={css({ fontStyle: 'italic' })}>
                            no restrictions
                        </li>
                    </ul>
                    <b>Invalid/no payment method</b>
                    <ul>
                        <li>access to the app is possible</li>
                        <li>
                            only parking sessions without a tariff are possible
                        </li>
                        <li>no new products can be purchased in the app</li>
                        <li>existing products remain valid</li>
                    </ul>
                    <b>BLOCKED</b>
                    <ul>
                        <li>access to the app is possible</li>
                        <li>no new products can be purchased in the app</li>
                        <li>no parking sessions are possible</li>
                        <li>existing products are suspended</li>
                    </ul>
                    <b>ARCHIVED</b>
                    <ul>
                        <li>access to the app is not possible</li>
                        <li>no parking sessions are possible</li>
                    </ul>
                </>
            }
        />
    );
}

function CustomerStateWithoutAppInfo() {
    return (
        <Localized
            de={
                <>
                    <b>AKTIV</b>
                    <p className={css({ fontStyle: 'italic' })}>
                        keine Einschränkung
                    </p>
                    <b>GESPERRT</b>
                    <ul>
                        <li>Kein Parkvorgang möglich</li>
                        <li>Bestehende Produkte sind ausgesetzt</li>
                    </ul>
                    <b>ARCHIVIERT</b>
                    <ul>
                        <li>Kein Parkvorgang möglich</li>
                    </ul>
                </>
            }
            fr={
                <>
                    <b>ACTIF</b>
                    <p className={css({ fontStyle: 'italic' })}>
                        aucune restriction
                    </p>
                    <b>VERROUILLÉ</b>
                    <ul>
                        <li>impossible d’effectuer des procédures</li>
                        <li>les produits existants sont suspendus</li>
                    </ul>
                    <b>ARCHIVÉ</b>
                    <ul>
                        <li>impossible d’effectuer des procédures</li>
                    </ul>
                </>
            }
            it={
                <>
                    <b>ATTIVO</b>
                    <p className={css({ fontStyle: 'italic' })}>
                        nessuna restrizione
                    </p>
                    <b>BLOCCATO</b>
                    <ul>
                        <li>non è possibile effettuare soste</li>
                        <li>i prodotti esistenti sono sospesi</li>
                    </ul>
                    <b>ARCHIVIATO</b>
                    <ul>
                        <li>non è possibile effettuare soste</li>
                    </ul>
                </>
            }
            en={
                <>
                    <b>ACTIVE</b>
                    <p className={css({ fontStyle: 'italic' })}>
                        no restrictions
                    </p>
                    <b>BLOCKED</b>
                    <ul>
                        <li>No parking sessions are possible</li>
                        <li>Existing products are suspended</li>
                    </ul>
                    <b>ARCHIVED</b>
                    <ul>
                        <li>No parking sessions are possible</li>
                    </ul>
                </>
            }
        />
    );
}

function EmailField({
    readonly,
    control,
    emailInvalidMessage,
    longTooManyCharsMessage,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    emailInvalidMessage: string;
    longTooManyCharsMessage: string;
}) {
    return (
        <ReactHookFormTextField
            readonly={readonly}
            label={<Localized de="E-Mail" fr="E-mail" it="E-mail" en="Email" />}
            name={Fields.Email}
            control={control}
            rules={{
                validate: {
                    isEmailValid: (value: string | null) =>
                        value !== '' && value !== null
                            ? EmailValidator.validate(value) ||
                              emailInvalidMessage
                            : true,
                },
                maxLength: {
                    value: MAX_LENGTH_LONG,
                    message: longTooManyCharsMessage,
                },
            }}
            trimOnBlur={true}
        />
    );
}

function RoleField({
    roles,
    readonly,
    control,
    legalForm,
}: {
    roles: ParkingaboRole[];
    readonly: boolean;
    control: Control<FieldValues, object>;
    legalForm: CustomerTenantLegalForm;
}) {
    const { language } = useLanguage();
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);

    const activeRoles = roles
        .filter(role => {
            switch (role.allowedCustomerLegalForm) {
                case TenantAllowedCustomerLegalForm.COMPANY_ONLY:
                    return legalForm === CustomerTenantLegalForm.COMPANY;
                case TenantAllowedCustomerLegalForm.PRIVATE_ONLY:
                    return legalForm === CustomerTenantLegalForm.PRIVATE;
                case TenantAllowedCustomerLegalForm.PRIVATE_AND_COMPANY:
                    return true;
            }
        })
        .filter(role => !role.deactivated);
    if (activeRoles.length <= 1) {
        return null;
    }

    const rolesForOptions = readonly ? roles : activeRoles;

    return (
        <OperatorFormSelect
            readonly={readonly}
            label={<Localized de="Rolle" fr="Rôle" it="Ruolo" en="Role" />}
            name={Fields.RoleId}
            control={control}
            options={rolesForOptions
                .sort((a, b) =>
                    a.name[language].localeCompare(b.name[language]),
                )
                .map(role => ({
                    display: (
                        <Box>
                            <Localized {...role.name} />
                            {role.deactivated && (
                                <Localized
                                    de=" — INAKTIV"
                                    fr=" — INACTIVE"
                                    it=" — INATTIVA"
                                    en=" — INACTIVE"
                                />
                            )}
                        </Box>
                    ),
                    value: role.roleId,
                }))}
            rules={{
                required: {
                    value: true,
                    message: mandatoryFieldMessage,
                },
            }}
        />
    );
}

function ReferenceField({
    readonly,
    control,
    shortTooManyCharsMessage,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    shortTooManyCharsMessage: string;
}) {
    return (
        <ReactHookFormTextField
            sx={{
                marginTop: '48px !important',
            }}
            readonly={readonly}
            label={
                <Localized
                    de="Referenz"
                    fr="Référence"
                    it="Referenza"
                    en="Reference"
                />
            }
            name={Fields.Reference}
            control={control}
            trimOnBlur={true}
            rules={{
                maxLength: {
                    value: MAX_LENGTH_SHORT,
                    message: shortTooManyCharsMessage,
                },
            }}
        />
    );
}

function PhoneField({
    readonly,
    control,
    shortTooManyCharsMessage,
    phoneInvalidCharsMessage,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    shortTooManyCharsMessage: string;
    phoneInvalidCharsMessage: string;
}) {
    return (
        <ReactHookFormTextField
            readonly={readonly}
            label={
                <Localized
                    de="Telefon"
                    fr="Téléphone"
                    it="Telefono"
                    en="Phone"
                />
            }
            name={Fields.Phone}
            control={control}
            rules={{
                maxLength: {
                    value: MAX_LENGTH_SHORT,
                    message: shortTooManyCharsMessage,
                },
                validate: {
                    isPhoneValid: (value: string) => {
                        if (value === '') {
                            return true;
                        }

                        if (!/^[+0-9][0-9 ]*$/.test(value)) {
                            return phoneInvalidCharsMessage;
                        }

                        return true;
                    },
                },
            }}
            trimOnBlur={true}
        />
    );
}

function CompanyDataContent({
    readonly,
    control,
    longTooManyCharsMessage,
    shortTooManyCharsMessage,
    statusTranslation,
    parkingaboUser,
    roleOfUser,
    mandatoryFieldMessage,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    longTooManyCharsMessage: string;
    shortTooManyCharsMessage: string;
    statusTranslation: string;
    parkingaboUser?: ParkingaboUser;
    roleOfUser: ParkingaboRole;
    mandatoryFieldMessage: string;
}) {
    return (
        <>
            <DivideBar
                largeMarginTop={false}
                title={
                    <Localized
                        de="Kontodetails"
                        fr="Détails du compte"
                        it="Dettagli conto"
                        en="Account details"
                    />
                }
            />
            {parkingaboUser && (
                <ParkingaboUserLifecycleInfo
                    autoWrittenOffAt={parkingaboUser.autoWrittenOffAt}
                    archivedAt={parkingaboUser.archivedAt}
                />
            )}
            <StateAndIdFields
                parkingaboUser={parkingaboUser}
                control={control}
                statusTranslation={statusTranslation}
                roleOfUser={roleOfUser}
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized
                        de="Firmenname 1"
                        fr="Nom de l'entreprise 1"
                        it="Nome dell'azienda 1"
                        en="Company name 1"
                    />
                }
                name={Fields.Company1}
                autoFocus={true}
                control={control}
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                    maxLength: {
                        value: MAX_LENGTH_LONG,
                        message: longTooManyCharsMessage,
                    },
                }}
                trimOnBlur={true}
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized
                        de="Firmenname 2"
                        fr="Nom de l'entreprise 2"
                        it="Nome dell'azienda 2"
                        en="Company name 2"
                    />
                }
                name={Fields.Company2}
                control={control}
                rules={{
                    maxLength: {
                        value: MAX_LENGTH_LONG,
                        message: longTooManyCharsMessage,
                    },
                }}
                trimOnBlur={true}
            />
            <ReferenceField
                readonly={readonly}
                control={control}
                shortTooManyCharsMessage={shortTooManyCharsMessage}
            />
        </>
    );
}

function ContactPersonContent({
    readonly,
    control,
    longTooManyCharsMessage,
    shortTooManyCharsMessage,
    emailInvalidMessage,
    phoneInvalidCharsMessage,
    mandatoryFieldMessage,
    roleOfUser,
    parkingaboUser,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    longTooManyCharsMessage: string;
    shortTooManyCharsMessage: string;
    emailInvalidMessage: string;
    phoneInvalidCharsMessage: string;
    mandatoryFieldMessage: string;
    roleOfUser: ParkingaboRole;
    parkingaboUser?: ParkingaboUser;
}) {
    return (
        <>
            <DivideBar
                largeMarginTop={true}
                title={
                    <Localized
                        de="Kontaktperson"
                        fr="Personne de contact"
                        it="Persona di contatto"
                        en="Contact person"
                    />
                }
            />
            <FirstNameLastNameFields
                readonly={readonly}
                control={control}
                longTooManyCharsMessage={longTooManyCharsMessage}
                mandatoryFieldMessage={mandatoryFieldMessage}
            />
            {roleOfUser.paymentMode !==
                ParkingaboCustomerPaymentMode.APP_PAYMENT && (
                <>
                    <EmailField
                        control={control}
                        readonly={readonly}
                        emailInvalidMessage={emailInvalidMessage}
                        longTooManyCharsMessage={longTooManyCharsMessage}
                    />
                    <EmailSuppressionTag
                        suppression={parkingaboUser?.emailSuppression ?? null}
                        tooltipPlacement={'right'}
                    />
                </>
            )}
            <PhoneField
                readonly={readonly}
                control={control}
                shortTooManyCharsMessage={shortTooManyCharsMessage}
                phoneInvalidCharsMessage={phoneInvalidCharsMessage}
            />
        </>
    );
}

function UserDataContent({
    readonly,
    control,
    longTooManyCharsMessage,
    shortTooManyCharsMessage,
    emailInvalidMessage,
    phoneInvalidCharsMessage,
    statusTranslation,
    parkingaboUser,
    roleOfUser,
    mandatoryFieldMessage,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    longTooManyCharsMessage: string;
    shortTooManyCharsMessage: string;
    emailInvalidMessage: string;
    phoneInvalidCharsMessage: string;
    statusTranslation: string;
    parkingaboUser?: ParkingaboUser;
    roleOfUser: ParkingaboRole;
    mandatoryFieldMessage: string;
}) {
    return (
        <>
            <DivideBar
                largeMarginTop={false}
                title={
                    <Localized
                        de="Kontodetails"
                        fr="Détails du compte"
                        it="Dettagli conto"
                        en="Account details"
                    />
                }
            />
            {parkingaboUser && (
                <ParkingaboUserLifecycleInfo
                    autoWrittenOffAt={parkingaboUser.autoWrittenOffAt}
                    archivedAt={parkingaboUser.archivedAt}
                />
            )}
            <StateAndIdFields
                parkingaboUser={parkingaboUser}
                control={control}
                statusTranslation={statusTranslation}
                roleOfUser={roleOfUser}
            />
            <FirstNameLastNameFields
                readonly={readonly}
                control={control}
                longTooManyCharsMessage={longTooManyCharsMessage}
                mandatoryFieldMessage={mandatoryFieldMessage}
            />
            <ReferenceField
                readonly={readonly}
                control={control}
                shortTooManyCharsMessage={shortTooManyCharsMessage}
            />
            {roleOfUser.paymentMode !==
                ParkingaboCustomerPaymentMode.APP_PAYMENT && (
                <>
                    <EmailField
                        control={control}
                        readonly={readonly}
                        emailInvalidMessage={emailInvalidMessage}
                        longTooManyCharsMessage={longTooManyCharsMessage}
                    />
                    <EmailSuppressionTag
                        suppression={parkingaboUser?.emailSuppression ?? null}
                        tooltipPlacement={'right'}
                    />
                </>
            )}
            <PhoneField
                readonly={readonly}
                control={control}
                shortTooManyCharsMessage={shortTooManyCharsMessage}
                phoneInvalidCharsMessage={phoneInvalidCharsMessage}
            />
        </>
    );
}

function SettingsContent({
    readonly,
    control,
    hasAppLogin,
    isCreate,
    roles,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    hasAppLogin: boolean;
    isCreate: boolean;
    roles: ParkingaboRole[];
}) {
    return (
        <>
            <DivideBar
                largeMarginTop={hasAppLogin}
                title={
                    <Localized
                        de="Einstellungen"
                        fr="Paramètres"
                        it="Impostazioni"
                        en="Settings"
                    />
                }
            />
            <RoleField
                roles={roles}
                readonly={isCreate || readonly}
                control={control}
                legalForm={CustomerTenantLegalForm.PRIVATE}
            />
            <OperatorFormSelect
                readonly={readonly}
                label={
                    <Localized
                        de="Sprache"
                        fr="Langue"
                        it="Lingua"
                        en="Language"
                    />
                }
                name={Fields.Language}
                control={control}
                options={[
                    {
                        display: (
                            <Localized
                                de="Deutsch"
                                fr="Allemande"
                                it="Tedesco"
                                en="German"
                            />
                        ),
                        value: Language.DE,
                    },
                    {
                        display: (
                            <Localized
                                de="Französisch"
                                fr="Français"
                                it="Francese"
                                en="French"
                            />
                        ),
                        value: Language.FR,
                    },
                    {
                        display: (
                            <Localized
                                de="Italienisch"
                                fr="Italienne"
                                it="Italiano"
                                en="Italian"
                            />
                        ),
                        value: Language.IT,
                    },
                    {
                        display: (
                            <Localized
                                de="Englisch"
                                fr="Anglaise"
                                it="Inglese"
                                en="English"
                            />
                        ),
                        value: Language.EN,
                    },
                ]}
            />
        </>
    );
}

function MiscellaneousContent({
    readonly,
    control,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
}) {
    return (
        <>
            <DivideBar
                largeMarginTop={true}
                title={
                    <Localized
                        de="Diverses"
                        fr="Divers"
                        it="Varie"
                        en="Miscellaneous"
                    />
                }
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <>
                        <Localized
                            de="Interne Bemerkungen"
                            fr="Remarques internes"
                            it="Osservazioni interne"
                            en="Internal remarks"
                        />
                        <Tooltip
                            title={
                                <Localized
                                    de="Die interne Bemerkungen sind nur hier im Parkingportal sichtbar."
                                    fr="Les remarques internes sont uniquement visibles ici dans Parkingportal."
                                    it="Le osservazioni interne sono visibili unicamente qui in Parkingportal."
                                    en="The internal remarks are only visible here in Parkingportal."
                                />
                            }
                            placement="right"
                        >
                            <InfoIcon
                                sx={{
                                    color: theme => theme.palette.blue.main,
                                    marginLeft: 1,
                                }}
                            />
                        </Tooltip>
                    </>
                }
                name={Fields.Remarks}
                control={control}
                multiline={true}
                rows={3}
                trimOnBlur={true}
            />
        </>
    );
}

function AppContent({
    parkingaboUser,
    paymentTranslation,
    roleOfUser,
    readonly,
}: {
    parkingaboUser: ParkingaboUser;
    paymentTranslation: string;
    roleOfUser: ParkingaboRole;
    readonly: boolean;
}) {
    const hasLogin = parkingaboUser.logins && parkingaboUser.logins.length > 0;
    const hasConfirmedLogin =
        parkingaboUser.logins &&
        parkingaboUser.logins.filter(
            login => login.state === ParkingaboLoginState.CONFIRMED,
        ).length > 0;
    return (
        <>
            <DivideBar
                largeMarginTop={false}
                title={<Localized de="App" fr="App" it="App" en="App" />}
            />
            {parkingaboUser.autoAppDeletedAt != null && (
                <Alert severity="info">
                    <Localized
                        de={`Der Zugang via App und das Zahlungsmittel wurden am ${Formatter.dayMonthYear(
                            DateTime.fromISO(parkingaboUser.autoAppDeletedAt),
                        )} autom. gelöscht.`}
                        fr={`L'accès via l'app et le moyen de paiement ont été supprimés automatiquement le ${Formatter.dayMonthYear(
                            DateTime.fromISO(parkingaboUser.autoAppDeletedAt),
                        )}.`}
                        it={`L'accesso via app e il mezzo di pagamento sono stati cancellati automaticamente il ${Formatter.dayMonthYear(
                            DateTime.fromISO(parkingaboUser.autoAppDeletedAt),
                        )}.`}
                        en={`Access via the app and the payment method were automatically deleted on ${Formatter.dayMonthYear(
                            DateTime.fromISO(parkingaboUser.autoAppDeletedAt),
                        )}.`}
                    />
                </Alert>
            )}
            {roleOfUser.paymentMode ===
                ParkingaboCustomerPaymentMode.APP_PAYMENT &&
                !roleOfUser.hasAppSignupEmailDomains &&
                !readonly &&
                !hasConfirmedLogin && (
                    <ReadOnlyTextField
                        label={
                            <Localized
                                de="Registrierung"
                                fr="Inscription"
                                it="Registrazione"
                                en="Registration"
                            />
                        }
                        value={
                            <RegistrationTypeValue
                                vehicleIdentification={
                                    roleOfUser.vehicleIdentification
                                }
                                customerNr={parkingaboUser.customerNr}
                            />
                        }
                    />
                )}
            {parkingaboUser?.role.paymentMode ===
                ParkingaboCustomerPaymentMode.APP_PAYMENT && (
                <TextField
                    disabled={true}
                    label={
                        <Localized
                            de="Zahlungsmittel"
                            fr="Moyen de paiement"
                            it="Mezzo di pagamento"
                            en="Payment mean"
                        />
                    }
                    defaultValue={hasLogin ? paymentTranslation : EmDash}
                    slotProps={{
                        input: hasLogin
                            ? {
                                  startAdornment: (
                                      <InputAdornment position="start">
                                          <PaymentMeansStateIcon
                                              state={
                                                  parkingaboUser?.paymentMeansState
                                              }
                                          />
                                      </InputAdornment>
                                  ),
                              }
                            : undefined,
                    }}
                />
            )}
            <ReadOnlyTextField
                label={'APP-LOGIN'}
                value={
                    hasLogin
                        ? parkingaboUser.logins.map(login => (
                              <div
                                  key={login.loginId}
                                  className={css({
                                      marginTop: '0px !important',
                                      marginBottom: '8px',
                                      ...Typo.robotoMedium,
                                      color: ColorHex.darkblue,
                                  })}
                              >
                                  <p
                                      className={css({
                                          marginTop: '0px',
                                          marginBottom: '8px',
                                      })}
                                  >
                                      <EmailLabelFromLogin login={login} />
                                  </p>
                                  <EmailSuppressionTag
                                      suppression={login.emailSuppression}
                                      tooltipPlacement="right"
                                  />
                                  {login.requestedEmail && (
                                      <p
                                          className={css({
                                              fontStyle: 'italic',
                                              marginTop: '0px',
                                              marginBottom: '8px',
                                          })}
                                      >
                                          <Localized
                                              de={`${login.requestedEmail} (noch nicht bestätigt)`}
                                              fr={`${login.requestedEmail} (non encore confirmé)`}
                                              it={`${login.requestedEmail} (non ancora confermato)`}
                                              en={`${login.requestedEmail} (not yet confirmed)`}
                                          />
                                      </p>
                                  )}
                                  <EmailSuppressionTag
                                      suppression={
                                          login.requestedEmailSuppression
                                      }
                                      tooltipPlacement="right"
                                  />
                              </div>
                          ))
                        : EmDash
                }
            />
        </>
    );
}

function RegistrationTypeValue({
    vehicleIdentification,
    customerNr,
}: {
    vehicleIdentification: VehicleIdentification[];
    customerNr: string;
}) {
    if (vehicleIdentification.includes(VehicleIdentification.BADGE)) {
        return (
            <Localized
                de="Parkingabo-Badge (QR-Code)"
                fr="Badge Parkingabo (code QR)"
                it="Badge Parkingabo (codice QR)"
                en="Parkingabo badge (QR code)"
            />
        );
    } else {
        const url = parkingaboUrl() + '/c/' + customerNr;
        return (
            <>
                <a href={url} target="_blank" rel="noreferrer">
                    {url}
                </a>
            </>
        );
    }
}

function isLegalFormToggleVisible(
    legalForm: CustomerTenantLegalForm | null,
    allowedLegalForm: TenantAllowedCustomerLegalForm,
) {
    if (
        allowedLegalForm === TenantAllowedCustomerLegalForm.PRIVATE_AND_COMPANY
    ) {
        return true;
    }

    return (
        legalForm !== null &&
        ((legalForm === CustomerTenantLegalForm.COMPANY &&
            allowedLegalForm === TenantAllowedCustomerLegalForm.PRIVATE_ONLY) ||
            (legalForm === CustomerTenantLegalForm.PRIVATE &&
                allowedLegalForm ===
                    TenantAllowedCustomerLegalForm.COMPANY_ONLY))
    );
}

const MAX_LENGTH_SHORT = 25;
const MAX_LENGTH_LONG = 50;

enum ROLE_CHANGE_ERROR {
    ROLE_APP_PAYMENT_MODE_CHANGE_ERROR = 'ROLE_APP_PAYMENT_MODE_CHANGE_ERROR',
    ROLE_BADGES_IN_USE_ERROR = 'ROLE_BADGES_IN_USE_ERROR',
    ROLE_INCOMPATIBLE_PAYMENT_MODE_ERROR_MESSAGE = 'ROLE_INCOMPATIBLE_PAYMENT_MODE_ERROR_MESSAGE',
    ROLE_RESTRICTED_PRODUCT_ERROR_MESSAGE = 'ROLE_RESTRICTED_PRODUCT_ERROR_MESSAGE',
    ROLE_DIFFERENT_APP_SIGNUP_EMAIL_DOMAINS_ERROR = 'ROLE_DIFFERENT_APP_SIGNUP_EMAIL_DOMAINS_ERROR',
}

export function OperatorParkingaboUserForm({
    title,
    Icon,
    parkingaboUser,
    onSubmit,
    submitState,
    readonly,
    roleOfUser,
    roles,
    onDirtyStateChange,
    onClose,
    detailHeader,
    detailBar,
}: OperatorParkingaboUserFormProps) {
    const { language } = useLanguage();
    const isCreate = !parkingaboUser?.customerNr;

    const {
        formInfo: {
            control,
            handleSubmit,
            watch,
            formState: { isDirty, isValid },
            reset,
            resetField,
            setValue,
        },
        genericSubmitError,
    } = useEasyForm(onDirtyStateChange, submitState, language, {
        defaultValues: deriveFormValuesOrUseDefault(
            parkingaboUser,
            roleOfUser.roleId,
            roleOfUser.allowedCustomerLegalForm,
            language,
        ),
    });
    const legalFormValue = watch(Fields.LegalForm);
    const initialBalance = watch(Fields.InitialBalance);
    const selectedRoleId = watch(Fields.RoleId);

    useEffect(() => {
        if (initialBalance.amount == 0) {
            setValue(Fields.InitialBalance, {
                [InitialBalanceFields.Amount]: 0,
                [InitialBalanceFields.Remark]: '',
            });
            resetField(Fields.InitialBalance);
        }
    }, [initialBalance.amount, resetField, setValue]);

    useServerSuccessEffect(submitState, newValues => {
        reset(
            deriveFormValuesOrUseDefault(
                newValues,
                roleOfUser.roleId,
                roleOfUser.allowedCustomerLegalForm,
                language,
            ),
        );
    });

    useEffect(() => {
        if (parkingaboUser) {
            reset(
                deriveFormValuesOrUseDefault(
                    parkingaboUser,
                    roleOfUser.roleId,
                    roleOfUser.allowedCustomerLegalForm,
                    language,
                ),
            );
        }
    }, [
        language,
        parkingaboUser,
        reset,
        roleOfUser.roleId,
        roleOfUser.allowedCustomerLegalForm,
    ]);

    const paymentTranslation = useTranslation(
        getTranslationForPaymeantState(parkingaboUser?.paymentMeansState),
    );
    const statusTranslation = useTranslation(
        getTranslationOfCustomerState(
            parkingaboUser?.state,
            parkingaboUser?.onboardingCompleteTime,
            parkingaboUser?.role.paymentMode,
        ),
    );
    const emailInvalidMessage = useTranslation({
        de: 'E-Mail nicht gültig.',
        fr: 'E-mail non valide.',
        it: 'E-mail non valida.',
        en: 'Email not valid.',
    });
    const phoneInvalidCharsMessage = useTranslation({
        de: 'Es sind nur Zahlen, + und Leerzeichen zulässig.',
        fr: 'Seuls les chiffres, + et les espaces sont autorisés.',
        it: 'Sono consentiti solo numeri, + e spazi.',
        en: 'Only numbers, +, and spaces are allowed.',
    });
    const shortTooManyCharsMessage = useTranslation(
        tooManyCharsMessage(MAX_LENGTH_SHORT),
    );
    const longTooManyCharsMessage = useTranslation(
        tooManyCharsMessage(MAX_LENGTH_LONG),
    );
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);

    const { operatorData, currentLogin } = useOperatorContext();

    const canCreateInitialBalance = parkingaboUser
        ? parkingaboUser.isAllowedInitialBalanceCreation
        : roleOfUser.paymentMode ===
              ParkingaboCustomerPaymentMode.APP_PAYMENT &&
          currentLogin.permissions.parkingaboInitialBalanceCreate &&
          operatorData.hasParkingaboUserInitialBalanceActive;

    const [openInternalBalanceWarning, setOpenInternalBalanceWarning] =
        useState(false);
    const [openRoleChangeAppWarning, setOpenRoleChangeAppWarning] =
        useState(false);
    const [roleChangeErrorOpen, setRoleChangeErrorOpen] = useState(true);

    useServerErrorEffect(submitState, (_statusCode, data) => {
        setOpenInternalBalanceWarning(false);
        if (hasRoleChangeError(data)) {
            setRoleChangeErrorOpen(true);
        }
    });

    const roleChangeError =
        genericSubmitError?.isGenericServerError === false
            ? genericSubmitError.issues.find(e =>
                  isRoleChangeError(e.issue.message),
              )
            : undefined;

    const isUnreversibleRoleChangeApp =
        !isCreate &&
        roleOfUser.paymentMode !== ParkingaboCustomerPaymentMode.APP_PAYMENT &&
        roles.find(role => role.roleId === selectedRoleId)?.paymentMode ===
            ParkingaboCustomerPaymentMode.APP_PAYMENT;

    return (
        <OperatorRoutedModalContent
            header={
                detailHeader && !isDirty ? (
                    detailHeader
                ) : (
                    <HeaderComponent
                        title={title}
                        Icon={Icon}
                        editMode={true}
                        buttons={
                            <HeaderCancelSaveButtons
                                onCancel={isCreate ? onClose : reset}
                                onSave={
                                    initialBalance?.amount &&
                                    !parkingaboUser?.initialBalance &&
                                    isValid
                                        ? () =>
                                              setOpenInternalBalanceWarning(
                                                  true,
                                              )
                                        : isValid && isUnreversibleRoleChangeApp
                                          ? () =>
                                                setOpenRoleChangeAppWarning(
                                                    true,
                                                )
                                          : onSubmit
                                            ? () => handleSubmit(onSubmit)()
                                            : undefined
                                }
                                loading={
                                    submitState.status === RequestStatus.PENDING
                                }
                                noDiscardDialog={isCreate}
                            />
                        }
                    />
                )
            }
            bar={detailBar}
            body={
                <>
                    <InternalBalanceWarningDialog
                        open={openInternalBalanceWarning}
                        onCancel={() => {
                            setValue(Fields.InitialBalance, {
                                [InitialBalanceFields.Amount]: 0,
                                [InitialBalanceFields.Remark]: '',
                            });
                            setOpenInternalBalanceWarning(false);
                        }}
                        onSubmit={() => {
                            setOpenInternalBalanceWarning(false);
                            if (isUnreversibleRoleChangeApp) {
                                setOpenRoleChangeAppWarning(true);
                            } else if (onSubmit) {
                                handleSubmit(onSubmit)();
                            }
                        }}
                        initialBalanceAmount={initialBalance.amount}
                    />
                    <RoleChangeAppWarningDialog
                        open={openRoleChangeAppWarning}
                        onCancel={() => setOpenRoleChangeAppWarning(false)}
                        onConfirm={() => {
                            setOpenRoleChangeAppWarning(false);
                            if (onSubmit) {
                                handleSubmit(onSubmit)();
                            }
                        }}
                    />
                    {roleChangeError && (
                        <RoleChangeErrorModal
                            open={roleChangeErrorOpen}
                            error={roleChangeError}
                            onConfirm={() => setRoleChangeErrorOpen(false)}
                        />
                    )}
                    {genericSubmitError != null && !roleChangeError && (
                        <GenericFormSubmitError
                            error={genericSubmitError}
                            submitState={submitState}
                        />
                    )}
                    {parkingaboUser?.needsProduct ? (
                        <Alert severity="warning" sx={{ margin: '0 0 20px 0' }}>
                            <Localized
                                de="Nächster Schritt: mindestens ein PRODUKT hinzufügen"
                                fr="Prochaine étape: ajouter au moins un PRODUIT"
                                it="Prossimo passo: aggiungere almeno un PRODOTTO"
                                en="Next step: add at least a PRODUCT"
                            />
                        </Alert>
                    ) : (
                        parkingaboUser?.needsVehicleWithBadge && (
                            <Alert
                                severity="warning"
                                sx={{ margin: '0 0 20px 0' }}
                            >
                                <Localized
                                    de="Letzter Schritt: mindestens ein FAHRZEUG MIT BADGE hinzufügen"
                                    fr="Dernière étape: ajoute au moins un VÉHICULE AVEC BADGE"
                                    it="Ultimo passo: aggiungere almeno un VEICOLO CON BADGE"
                                    en="Next step: add at least a VEHICLE WITH BADGE"
                                />
                            </Alert>
                        )
                    )}
                    {parkingaboUser &&
                        parkingaboUser.vehiclesWithMissingLps !== 0 && (
                            <Alert
                                severity="warning"
                                sx={{ margin: '0 0 20px 0' }}
                            >
                                {parkingaboUser.vehiclesWithMissingLps === 1 ? (
                                    <Localized
                                        de="1 Fahrzeug hat kein Kennzeichen und wird daher nicht von der LPR-Kamera erfasst"
                                        fr="1 véhicule n'a pas de plaque d'immatriculation, donc ne sera pas détecté par la caméra LPR"
                                        it="1 veicolo non ha una targa, quindi non verrà rilevato dalla telecamera LPR"
                                        en="1 vehicle has no licence plate and is not recognised by the LPR camera"
                                    />
                                ) : (
                                    <Localized
                                        de={`${parkingaboUser.vehiclesWithMissingLps} Fahrzeuge haben kein Kennzeichen und werden daher nicht von der LPR-Kamera erfasst`}
                                        fr={`${parkingaboUser.vehiclesWithMissingLps} véhicules n'ont pas de plaque d'immatriculation, donc ne seront pas détectés par la caméra LPR`}
                                        it={`${parkingaboUser.vehiclesWithMissingLps} veicoli non hanno una targa, quindi non verranno rilevati dalla telecamera LPR`}
                                        en={`${parkingaboUser.vehiclesWithMissingLps} vehicles have no licence plate and are not recognised by the LPR camera`}
                                    />
                                )}
                            </Alert>
                        )}
                    <FormContentLayout>
                        <FormLeftColumn>
                            {isLegalFormToggleVisible(
                                parkingaboUser?.legalForm ?? null,
                                roleOfUser.allowedCustomerLegalForm,
                            ) && <AccountTypeContent control={control} />}
                            {legalFormValue ===
                            CustomerTenantLegalForm.COMPANY ? (
                                <>
                                    <CompanyDataContent
                                        parkingaboUser={parkingaboUser}
                                        roleOfUser={roleOfUser}
                                        control={control}
                                        readonly={readonly}
                                        statusTranslation={statusTranslation}
                                        longTooManyCharsMessage={
                                            longTooManyCharsMessage
                                        }
                                        shortTooManyCharsMessage={
                                            shortTooManyCharsMessage
                                        }
                                        mandatoryFieldMessage={
                                            mandatoryFieldMessage
                                        }
                                    />
                                    <ContactPersonContent
                                        roleOfUser={roleOfUser}
                                        control={control}
                                        readonly={readonly}
                                        longTooManyCharsMessage={
                                            longTooManyCharsMessage
                                        }
                                        shortTooManyCharsMessage={
                                            shortTooManyCharsMessage
                                        }
                                        emailInvalidMessage={
                                            emailInvalidMessage
                                        }
                                        phoneInvalidCharsMessage={
                                            phoneInvalidCharsMessage
                                        }
                                        mandatoryFieldMessage={
                                            mandatoryFieldMessage
                                        }
                                        parkingaboUser={parkingaboUser}
                                    />
                                </>
                            ) : (
                                <UserDataContent
                                    parkingaboUser={parkingaboUser}
                                    control={control}
                                    readonly={readonly}
                                    roleOfUser={roleOfUser}
                                    statusTranslation={statusTranslation}
                                    emailInvalidMessage={emailInvalidMessage}
                                    phoneInvalidCharsMessage={
                                        phoneInvalidCharsMessage
                                    }
                                    longTooManyCharsMessage={
                                        longTooManyCharsMessage
                                    }
                                    shortTooManyCharsMessage={
                                        shortTooManyCharsMessage
                                    }
                                    mandatoryFieldMessage={
                                        mandatoryFieldMessage
                                    }
                                />
                            )}
                        </FormLeftColumn>
                        <FormRightColumn>
                            {(canCreateInitialBalance ||
                                parkingaboUser?.initialBalance) && (
                                <InitialBalanceContent
                                    readonly={readonly}
                                    control={control}
                                    canCreateInitialBalance={
                                        canCreateInitialBalance
                                    }
                                    hasAmountDefined={Boolean(
                                        initialBalance.amount,
                                    )}
                                />
                            )}
                            {parkingaboUser?.hasAppLogin && (
                                <AppContent
                                    parkingaboUser={parkingaboUser}
                                    paymentTranslation={paymentTranslation}
                                    roleOfUser={roleOfUser}
                                    readonly={readonly}
                                />
                            )}
                            <SettingsContent
                                readonly={readonly}
                                control={control}
                                hasAppLogin={Boolean(
                                    parkingaboUser?.hasAppLogin,
                                )}
                                isCreate={isCreate}
                                roles={roles}
                            />
                            <MiscellaneousContent
                                readonly={readonly}
                                control={control}
                            />
                        </FormRightColumn>
                    </FormContentLayout>
                </>
            }
        />
    );
}

function RoleChangeErrorModal({
    open,
    onConfirm,
    error,
}: {
    open: boolean;
    onConfirm: () => void;
    error: FormGlobalViolationEntry;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Rollenwechsel nicht möglich"
                    fr="Impossible de changer de rôle"
                    it="Cambio ruolo non possibile"
                    en="Role change not possible"
                />
            }
            confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
            onConfirm={onConfirm}
            dialogVersion={getRoleChangeErrorColor(error.issue.message)}
        >
            <p>
                <Localized {...error.issue.humanReadableMessage.byLanguage} />
            </p>
        </ConfirmationDialog>
    );
}

function RoleChangeAppWarningDialog({
    open,
    onCancel,
    onConfirm,
}: {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="UNWIDERRUFLICHE ÄNDERUNG"
                    fr="MODIFICATION IRRÉVERSIBLE"
                    it="MODIFICA IRREVERSIBILE"
                    en="UNREVERSIBLE CHANGE"
                />
            }
            confirmLabel={
                <Localized
                    de="Weiter"
                    fr="Continuer"
                    it="Continua"
                    en="Continue"
                />
            }
            cancelLabel={
                <Localized de="Zurück" fr="Retour" it="Indietro" en="Back" />
            }
            onConfirm={onConfirm}
            onCancel={onCancel}
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <p>
                <Localized
                    de="Die neu ausgewählte Rolle sieht die Nutzung der App vor. Wenn Sie diese Änderung bestätigen, ist es künftig nicht mehr möglich, diesem Benutzerkonto Rollen ohne App zuzuweisen."
                    fr="Le nouveau rôle sélectionné prévoit l'utilisation de l'app. Si vous confirmez cette modification, il ne sera plus possible à l'avenir d'attribuer à ce compte utilisateur des rôles sans app."
                    it="Il nuovo ruolo selezionato prevede l'utilizzo dell'app. Se si conferma questa modifica in futuro non sarà più possibile attribuire a questo conto utente dei ruoli senza app."
                    en="The new role you have selected requires the use of the app. If you confirm this change, it will no longer be possible to assign roles that do not require the app to this user account."
                />
            </p>
        </ConfirmationDialog>
    );
}

function hasRoleChangeError(validationData: ValidationData | null) {
    return (
        validationData?.violations?.some(
            violation =>
                isFormGlobalViolationEntry(violation) &&
                isRoleChangeError(violation.issue.message),
        ) ?? false
    );
}

function getRoleChangeErrorColor(message: string) {
    switch (getRoleChangeError(message)) {
        case ROLE_CHANGE_ERROR.ROLE_APP_PAYMENT_MODE_CHANGE_ERROR:
        case ROLE_CHANGE_ERROR.ROLE_DIFFERENT_APP_SIGNUP_EMAIL_DOMAINS_ERROR:
            return ConfirmationDialogVersions.ERROR;
        case ROLE_CHANGE_ERROR.ROLE_INCOMPATIBLE_PAYMENT_MODE_ERROR_MESSAGE:
        case ROLE_CHANGE_ERROR.ROLE_RESTRICTED_PRODUCT_ERROR_MESSAGE:
        case ROLE_CHANGE_ERROR.ROLE_BADGES_IN_USE_ERROR:
        case null:
            return ConfirmationDialogVersions.SOFT_WARNING;
    }
}

function isRoleChangeError(message: string) {
    return getRoleChangeError(message) !== null;
}

function getRoleChangeError(message: string) {
    return Object.values(ROLE_CHANGE_ERROR).find(e => e === message) ?? null;
}

function AccountTypeContent({
    control,
}: {
    control: Control<FieldValues, object>;
}) {
    return (
        <>
            <DivideBar
                largeMarginTop={false}
                title={
                    <Localized
                        de="Kontoart"
                        fr="Type de compte"
                        it="Tipo di conto"
                        en="Account type"
                    />
                }
            />
            <Controller
                control={control}
                name="legalForm"
                render={({ field }) => (
                    <ToggleButtonGroup
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                        exclusive={true}
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <Tooltip
                            title={
                                <Localized
                                    de="Für die Parkrechte der privaten Fahrzeuge des Benutzers."
                                    fr="Pour les droits de stationnement des véhicules privés de l'utilisateur."
                                    it="Per i diritti di parcheggio dei veicoli privati dell'utente."
                                    en="For parking rights for the user's private vehicles."
                                />
                            }
                            placement="bottom"
                        >
                            <ToggleButton
                                value="PRIVATE"
                                sx={{
                                    flex: 1,
                                    color: Colors.darkblue,
                                    backgroundColor: Colors.white,
                                    '&.Mui-selected': {
                                        color: Colors.white,
                                        backgroundColor: theme =>
                                            theme.palette.blue.main,
                                    },
                                }}
                            >
                                <Localized
                                    de="Privatkonto"
                                    fr="Privé"
                                    it="Privato"
                                    en="Private"
                                />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip
                            title={
                                <Localized
                                    de="Für die Parkrechte von Dienstfahrzeugen der Firma."
                                    fr="Pour les droits de stationnement des véhicules de service de l'entreprise."
                                    it="Per i diritti di parcheggio dei veicoli di servizio dell'azienda."
                                    en="For parking rights for service vehicles of company users."
                                />
                            }
                            placement="bottom"
                        >
                            <ToggleButton
                                value="COMPANY"
                                sx={{
                                    flex: 1,
                                    color: Colors.darkblue,
                                    backgroundColor: Colors.white,
                                    '&.Mui-selected': {
                                        color: Colors.white,
                                        backgroundColor: theme =>
                                            theme.palette.blue.main,
                                    },
                                }}
                            >
                                <Localized
                                    de="Firmenkonto"
                                    fr="Entreprise"
                                    it="Aziendale"
                                    en="Corporate"
                                />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                )}
            />
        </>
    );
}

function PaymentMeansStateIcon({
    state,
}: {
    state?: PaymentMeansState | null;
}) {
    if (!state) return <>{EmDash}</>;
    switch (state) {
        case PaymentMeansState.VALID:
            return <Circle color="success" />;
        case PaymentMeansState.INVALID:
            return <Circle color="warning" />;
        case PaymentMeansState.MISSING:
            return <Circle color="error" />;
    }
}

function deriveFormValuesOrUseDefault(
    inputValues: ParkingaboUser | CreateParkingaboUserInitialValues | undefined,
    roleId: string | undefined,
    allowedCustomerLegalForm: TenantAllowedCustomerLegalForm,
    language: Language,
): FieldValues {
    return {
        [Fields.Language]: inputValues?.language ?? language,
        [Fields.FirstName]: inputValues?.firstName ?? '',
        [Fields.LastName]: inputValues?.lastName ?? '',
        [Fields.Company1]: inputValues?.company1 ?? '',
        [Fields.Company2]: inputValues?.company2 ?? '',
        [Fields.RoleId]: inputValues?.roleId ?? roleId ?? null,
        [Fields.Email]: inputValues?.email ?? '',
        [Fields.Phone]: inputValues?.phone ?? '',
        [Fields.Reference]: inputValues?.reference ?? '',
        [Fields.Remarks]: inputValues?.remarks ?? '',
        [Fields.ID]: inputValues?.customerNr ?? null,
        [Fields.State]: inputValues?.state ?? null,
        [Fields.PaymentMeansState]: inputValues?.paymentMeansState ?? null,
        [Fields.LegalForm]:
            inputValues?.legalForm ??
            getDefaultLegalFormValueByTenantAllowedLegalForm(
                allowedCustomerLegalForm,
            ),
        [Fields.InitialBalance]: {
            [InitialBalanceFields.Amount]:
                inputValues?.initialBalance?.amount || 0,
            [InitialBalanceFields.Remark]:
                inputValues?.initialBalance?.remark || '',
        },
    };
}

export function DivideBar({
    title,
    largeMarginTop,
}: {
    title: JSX.Element;
    largeMarginTop: boolean;
}) {
    return (
        <Box
            sx={{
                marginTop: largeMarginTop ? '48px' : '24px',
                marginBottom: '48px',
                ...OperatorTypo.headingOne,
                textTransform: 'uppercase',
                fontWeight: '600',
                color: theme => theme.palette.blue.main,
                backgroundColor: theme => theme.palette.grey.A200,
            }}
        >
            <Box sx={{ textAlign: 'center', margin: '2px 0' }}>{title}</Box>
        </Box>
    );
}

function getDefaultLegalFormValueByTenantAllowedLegalForm(
    allowedCustomerLegalForm: TenantAllowedCustomerLegalForm,
): CustomerTenantLegalForm {
    switch (allowedCustomerLegalForm) {
        case TenantAllowedCustomerLegalForm.COMPANY_ONLY:
            return CustomerTenantLegalForm.COMPANY;
        case TenantAllowedCustomerLegalForm.PRIVATE_ONLY:
        case TenantAllowedCustomerLegalForm.PRIVATE_AND_COMPANY:
            return CustomerTenantLegalForm.PRIVATE;
    }
}

function EmailLabelFromLogin({
    login,
}: {
    login: ParkingaboLogin;
}): JSX.Element {
    switch (login.state) {
        case ParkingaboLoginState.CONFIRMED:
            return <>{login.email}</>;
        case ParkingaboLoginState.CREATED:
            return (
                <Box
                    sx={{
                        display: 'flex',
                        aligneItems: 'center',
                    }}
                >
                    <Localized
                        de={`${login.email} (nicht bestätigt)`}
                        fr={`${login.email} (non confirmé)`}
                        it={`${login.email} (non confermato)`}
                        en={`${login.email} (not confirmed)`}
                    />
                    <Tooltip
                        title={
                            <Localized
                                de="Durch erneutes Scannen des QR-Codes kann der Benutzer einen neuen Bestätigungslink anfordern oder die Registrierung wiederholen, um einen möglichen Fehler in der E-Mail-Adresse zu korrigieren."
                                fr="En scannant à nouveau le code QR, l'utilisateur peut demander un nouveau lien de confirmation ou répéter l'enregistrement pour corriger une éventuelle erreur dans l'adresse e-mail."
                                it="Scannerizzando nuovamente il codice QR l’utente può richiedere un nuovo link di conferma oppure ripetere la registrazione per correggere un eventuale errore nell’indirizzo e-mail."
                                en="By scanning the QR code again, the user can request a new confirmation link or repeat the registration to correct a possible error in the e-mail address."
                            />
                        }
                    >
                        <InfoIcon
                            sx={{
                                color: theme => theme.palette.blue.main,
                                marginLeft: 1,
                            }}
                        />
                    </Tooltip>
                </Box>
            );
        case ParkingaboLoginState.DELETED:
            return <>{EmDash}</>;
    }
}

function InitialBalanceContent({
    readonly,
    control,
    canCreateInitialBalance,
    hasAmountDefined,
}: {
    readonly: boolean;
    control: Control<FieldValues, object>;
    canCreateInitialBalance: boolean;
    hasAmountDefined: boolean;
}) {
    const remarkDisabled = !canCreateInitialBalance || !hasAmountDefined;
    return (
        <>
            <DivideBar
                largeMarginTop
                title={
                    <Localized
                        de="RESTWERT DER SALDOKARTE"
                        fr="VALEUR RÉSIDUELLE CARTE DE SOLDE"
                        it="VALORE RESIDUO CARTA A SALDO"
                        en="RESIDUAL VALUE BALANCE CARD"
                    />
                }
            />
            <ReactHookFormTextField
                type="number"
                readonly={readonly}
                label={
                    <Localized
                        de="Betrag (CHF)"
                        fr="Montant (CHF)"
                        it="Importo (CHF)"
                        en="Amount (CHF)"
                    />
                }
                slotProps={{ htmlInput: { step: 0.01 } }}
                name={`${Fields.InitialBalance}.${InitialBalanceFields.Amount}`}
                control={control}
                disabled={!canCreateInitialBalance}
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <>
                        <Localized
                            de="Referenz der alten Saldokarte"
                            fr="Référence ancienne carte de solde"
                            it="Riferimento vecchia carta a saldo"
                            en="Old balance card reference"
                        />
                        <Tooltip
                            title={
                                <Localized
                                    de="Diese Referenz wird auch im Kontoauszug vom Benutzer sichtbar sein."
                                    fr="Cette référence sera également visible sur le relevé de compte de l'utilisateur."
                                    it="Questo riferimento sarà visibile anche nell'estratto conto dell'utente."
                                    en="This reference will also be visible in the user's account statement."
                                />
                            }
                            placement="right"
                        >
                            <InfoIcon
                                sx={{
                                    color: theme => theme.palette.blue.main,
                                    marginLeft: 1,
                                }}
                            />
                        </Tooltip>
                    </>
                }
                placeholder={readonly || remarkDisabled ? EmDash : ''}
                name={`${Fields.InitialBalance}.${InitialBalanceFields.Remark}`}
                control={control}
                trimOnBlur={true}
                disabled={remarkDisabled}
            />
        </>
    );
}

function InternalBalanceWarningDialog({
    open,
    onCancel,
    onSubmit,
    initialBalanceAmount,
}: {
    open: boolean;
    onCancel: () => void;
    onSubmit?: () => void;
    initialBalanceAmount: number;
}) {
    return (
        <Dialog open={open} maxWidth="xs" onClose={onCancel}>
            <DialogTitle>
                <Localized
                    de="RESTWERT DER SALDOKARTE"
                    fr="VALEUR RÉSIDUELLE CARTE DE SOLDE"
                    it="VALORE RESIDUO CARTA A SALDO"
                    en="RESIDUAL VALUE BALANCE CARD"
                />
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <p>
                        <Localized
                            de={`Beim Speichern dieses Benutzers wird ihm CHF ${initialBalanceAmount} als Startguthaben aus dem Restwert der vorherigen Saldo-Karte gutgeschrieben.`}
                            fr={`En sauvegardant cet utilisateur, il sera crédité de ${initialBalanceAmount} CHF comme crédit initial correspondant à la valeur résiduelle de l'ancienne carte de solde.`}
                            it={`Salvando questo utente, gli verranno accreditati CHF ${initialBalanceAmount} come credito iniziale corrispondente al valore residuo della vecchia carta a saldo.`}
                            en={`By saving this user, he will be credited CHF ${initialBalanceAmount} as initial credit from the residual value of the old balance card.`}
                        />
                    </p>
                    <p>
                        <b>
                            <Localized
                                de="Wichtig: diese Aktion kann nicht rückgängig gemacht oder geändert werden."
                                fr="Important: cette action ne peut être ni annulée ni modifiée."
                                it="Importante: questa azione non può essere annullata o modificata."
                                en="Important: this action cannot be undone or modified."
                            />
                        </b>
                    </p>
                    <p>
                        <Localized
                            de="Möchten Sie fortfahren?"
                            fr="Voulez-vous continuer?"
                            it="Vuole continuare?"
                            en="Would you like to continue?"
                        />
                    </p>
                </DialogContentText>
                <DialogActions>
                    <Button onClick={onCancel}>
                        <Localized
                            de="Abbrechen"
                            fr="Annuler"
                            it="Annulla"
                            en="Cancel"
                        />
                    </Button>
                    <Button onClick={onSubmit}>
                        <Localized
                            de="Bestätigen"
                            fr="Confirmer"
                            it="Conferma"
                            en="Confirm"
                        />
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}
